// Upload plików: źródłem prawdy jest tablica File[] per pole (input służy
// tylko jako picker i jest zerowany po każdym wyborze — ponowny wybór tego
// samego pliku działa). Walidacja typu/rozmiaru/liczby z komunikatami PL,
// podgląd miniatur, licznik łącznego rozmiaru, wklejanie ze schowka.
//
// Komunikaty błędów wzorowane na formułach GOV.UK: mówią, co jest nie tak
// i co zrobić, zawsze przy konkretnym pliku, nigdy globalnie „błąd".

import { announce } from "./a11y.js";

const WARN_TOTAL_MB = 48;

const SVG_FILE =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M14 3v5h5"/><path d="M14 3H6.5A1.5 1.5 0 0 0 5 4.5v15A1.5 1.5 0 0 0 6.5 21h11a1.5 1.5 0 0 0 1.5-1.5V8z"/></svg>';
const SVG_X =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>';

function formatSize(bytes) {
    if (bytes < 1024 * 1024) return Math.max(1, Math.round(bytes / 1024)) + " kB";
    return (bytes / 1024 / 1024).toFixed(1).replace(".", ",") + " MB";
}

export function initUploads(form) {
    const stores = new Map(); // input name -> { files: File[], container, add }

    form.querySelectorAll(".brief__upload").forEach((container) => {
        const input = container.querySelector(".brief__uploadInput");
        const list = container.querySelector(".brief__uploadList");
        if (!input || !list) return;

        const maxFiles = Number(container.dataset.maxFiles || 1);
        const maxBytes = Number(container.dataset.maxMb || 10) * 1024 * 1024;
        const accept = (input.getAttribute("accept") || "")
            .split(",")
            .map((ext) => ext.trim().toLowerCase())
            .filter(Boolean);
        const formats = accept.map((ext) => ext.replace(".", "").toUpperCase()).join(", ");
        const store = { files: [], container };

        const errSlot = container.querySelector(".brief__fieldError");

        function showError(message) {
            if (errSlot) {
                errSlot.textContent = message;
                errSlot.hidden = false;
            }
        }
        function clearError() {
            if (errSlot) {
                errSlot.textContent = "";
                errSlot.hidden = true;
            }
        }

        function totalBytes() {
            let sum = 0;
            stores.forEach((s) => s.files.forEach((f) => (sum += f.size)));
            return sum;
        }

        function render() {
            list.innerHTML = "";
            store.files.forEach((file, index) => {
                const li = document.createElement("li");
                li.className = "brief__uploadItem";

                if (file.type.startsWith("image/") && file.type !== "image/heic") {
                    const img = document.createElement("img");
                    img.className = "brief__uploadThumb";
                    img.alt = "";
                    img.src = URL.createObjectURL(file);
                    img.onload = () => URL.revokeObjectURL(img.src);
                    img.onerror = () => {
                        const fallback = document.createElement("span");
                        fallback.className = "brief__uploadFileIcon";
                        fallback.innerHTML = SVG_FILE;
                        img.replaceWith(fallback);
                    };
                    li.appendChild(img);
                } else {
                    const icon = document.createElement("span");
                    icon.className = "brief__uploadFileIcon";
                    icon.innerHTML = SVG_FILE;
                    li.appendChild(icon);
                }

                const meta = document.createElement("span");
                meta.className = "brief__uploadMeta";
                const name = document.createElement("span");
                name.className = "brief__uploadName";
                name.textContent = file.name;
                name.title = file.name;
                const size = document.createElement("span");
                size.className = "brief__uploadSize";
                size.textContent = formatSize(file.size);
                meta.append(name, size);
                li.appendChild(meta);

                const remove = document.createElement("button");
                remove.type = "button";
                remove.className = "brief__uploadRemove";
                remove.setAttribute("aria-label", "Usuń plik " + file.name);
                remove.innerHTML = SVG_X;
                remove.addEventListener("click", () => {
                    store.files.splice(index, 1);
                    render();
                    announce("Plik usunięty: " + file.name);
                });
                li.appendChild(remove);

                list.appendChild(li);
            });
        }

        function addFiles(files) {
            clearError();
            const problems = [];
            Array.from(files).forEach((file) => {
                if (store.files.length >= maxFiles) {
                    problems.push(
                        maxFiles === 1
                            ? "Można dodać tylko jeden plik — usuń poprzedni, żeby podmienić."
                            : "Maksimum " + maxFiles + " plików — reszta pominięta."
                    );
                    return;
                }
                const ext = "." + (file.name.split(".").pop() || "").toLowerCase();
                if (accept.length && !accept.includes(ext)) {
                    problems.push(file.name + ": plik musi być w formacie " + formats + ".");
                    return;
                }
                if (file.size === 0) {
                    problems.push(file.name + " jest pusty — sprawdź plik i spróbuj ponownie.");
                    return;
                }
                if (file.size > maxBytes) {
                    problems.push(
                        file.name + " ma " + formatSize(file.size) +
                        " — maksimum to " + (maxBytes / 1024 / 1024).toFixed(0) +
                        " MB. Zmniejsz plik albo doślij później linkiem z maila."
                    );
                    return;
                }
                const duplicate = store.files.some(
                    (f) => f.name === file.name && f.size === file.size
                );
                if (duplicate) return;
                store.files.push(file);
            });

            if (problems.length) showError(problems.join(" "));
            render();

            if (totalBytes() > WARN_TOTAL_MB * 1024 * 1024) {
                announce("Dużo plików — wysyłka może chwilę potrwać.");
            } else if (!problems.length && files.length) {
                announce("Dodano " + files.length + " plik(ów).");
            }
        }

        store.add = addFiles;
        stores.set(input.name, store);

        input.addEventListener("change", () => {
            addFiles(input.files || []);
            input.value = "";
        });

        // widoczny przycisk wyboru (sterowanie głosem nie ma w co kliknąć,
        // gdy input jest schowany, a etykieta pola jest daleko)
        const pick = container.querySelector("[data-upload-pick]");
        if (pick) {
            pick.addEventListener("click", () => {
                if (!input.disabled) input.click();
            });
        }

        // strefa upuszczania widoczna zawsze, nie tylko przy przeciąganiu
        const drop = container.querySelector("[data-upload-drop]");
        if (drop) {
            drop.addEventListener("click", (event) => {
                if (event.target.closest("[data-upload-pick]")) return;
                if (!input.disabled) input.click();
            });
            ["dragenter", "dragover"].forEach((type) =>
                drop.addEventListener(type, (event) => {
                    event.preventDefault();
                    drop.classList.add("is-dragover");
                })
            );
            ["dragleave", "drop"].forEach((type) =>
                drop.addEventListener(type, (event) => {
                    event.preventDefault();
                    drop.classList.remove("is-dragover");
                })
            );
            drop.addEventListener("drop", (event) => {
                if (event.dataTransfer && event.dataTransfer.files) {
                    if (!input.disabled) addFiles(event.dataTransfer.files);
                }
            });
        }
    });

    // Wklejanie ze schowka: klient robi zrzut ekranu inspiracji albo zdjęcie
    // i wciska Ctrl+V. Trafia do pierwszego aktywnego pola na widocznym kroku.
    if (stores.size) {
        document.addEventListener("paste", (event) => {
            if (!event.clipboardData || !event.clipboardData.files.length) return;
            const target = event.target;
            if (target && target.matches && target.matches("input, textarea")) return;

            for (const store of stores.values()) {
                const input = store.container.querySelector(".brief__uploadInput");
                if (!input || input.disabled) continue;
                if (store.container.closest("[hidden]")) continue;
                event.preventDefault();
                store.add(event.clipboardData.files);
                return;
            }
        });
    }

    return {
        // mapa: nazwa inputu (np. "zdjecia[]") -> File[] (tylko z aktywnych pól)
        collect() {
            const out = {};
            stores.forEach((store, name) => {
                const input = store.container.querySelector(".brief__uploadInput");
                if (input && input.disabled) return;
                if (store.files.length) out[name] = store.files.slice();
            });
            return out;
        },
        hasFiles() {
            let any = false;
            stores.forEach((store) => {
                const input = store.container.querySelector(".brief__uploadInput");
                if (input && !input.disabled && store.files.length) any = true;
            });
            return any;
        },
    };
}
